'use client';

import { type MouseEvent, memo, type ReactNode, useCallback, useMemo, useState } from 'react';

import { type Board } from '@air/api/types';
import { VisuallyHidden } from '@air/primitive-visually-hidden';
import { cn } from '@air/utils-tailwind';

import { type ImageRenderProps } from '~/components/gallery/GalleryAssetCard';
import { imgixLoader } from '~/utils/imgixLoader';

export type GalleryBoardCardProps = {
  board: Board;
  isSelected?: boolean;
  onClick?: (board: Board, event: MouseEvent<HTMLButtonElement>) => void;
  onDoubleClick?: (board: Board, event: MouseEvent<HTMLButtonElement>) => void;
  renderImage?: (props: ImageRenderProps) => ReactNode;
};

/**
 * Gallery card for displaying a board with square aspect ratio.
 * Shows first thumbnail as background with title overlay.
 */
export const GalleryBoardCard = memo(
  ({ board, isSelected, onClick, onDoubleClick, renderImage }: GalleryBoardCardProps) => {
    const [imageError, setImageError] = useState(false);

    const thumbnailUrl = board.thumbnails?.[0];

    // Generate optimized image URL
    const optimizedThumbnailUrl = useMemo(() => {
      if (!thumbnailUrl) {
        return undefined;
      }

      return imgixLoader({ src: thumbnailUrl, width: 400 });
    }, [thumbnailUrl]);

    const handleClick = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        onClick?.(board, event);
      },
      [board, onClick],
    );

    const handleImageError = useCallback(() => {
      setImageError(true);
    }, []);

    const handleDoubleClick = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        onDoubleClick?.(board, event);
      },
      [board, onDoubleClick],
    );

    const showImage = optimizedThumbnailUrl && !imageError;

    return (
      <div
        className={cn(
          'group/board size-[150px] rounded-12 border bg-white p-1 dark:bg-neutral-2',
          isSelected ? 'border-2 border-brand-9' : 'border-neutral-5',
        )}
        data-board-id={board.id}
      >
        <div className="relative size-full overflow-hidden rounded-8 bg-neutral-4">
          {showImage &&
            (renderImage ? (
              renderImage({
                alt: board.title,
                className: 'absolute inset-0 size-full object-cover transition-transform group-hover/board:scale-105',
                onError: handleImageError,
                src: optimizedThumbnailUrl,
              })
            ) : (
              // biome-ignore lint/performance/noImgElement: External CDN image, not using Next.js Image
              <img
                alt={board.title}
                className="absolute inset-0 size-full object-cover transition-transform group-hover/board:scale-105"
                loading="lazy"
                onError={handleImageError}
                src={optimizedThumbnailUrl}
              />
            ))}

          <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-3">
            <span className="line-clamp-4 break-words font-semibold text-18 text-white leading-[156%]">
              {board.title}
            </span>
          </div>

          <button
            className="absolute inset-0 size-full bg-transparent"
            onClick={handleClick}
            onDoubleClick={handleDoubleClick}
            type="button"
          >
            <VisuallyHidden>Select {board.title} board</VisuallyHidden>
          </button>
        </div>
      </div>
    );
  },
);

GalleryBoardCard.displayName = 'GalleryBoardCard';
